import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import Fade from 'embla-carousel-fade';
import {
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  RotateCcw,
} from 'lucide-react';
import TournamentReport from '../tournament/TournamentReport';
import { InfoBottomSheet, BestPracticesBottomSheet } from './InfoBottomSheet';
import { TourProvider } from '../tour/TourContext';
import { GuidedTourLayer } from '../tour/GuidedTour';
import { stepsFor } from '../tour/tourSteps';
import {
  PlayerNamesProvider,
  type PlayerNamesMap,
} from '@/contexts/PlayerNamesContext';
import { NOT_PRESENT } from '@/lib/constants';
import { formatClock } from '@/lib/utils';
import { buildPlayerCrops } from '@/lib/transforms';
import type {
  SegmentStatistics,
  PredictionMode,
  PlayerSlot,
  PlayersCorrespondance,
} from '@/types/statistics';

/** One block's place on the match clock, in seconds into the recording. */
interface BlockSpan {
  start: number;
  end: number;
}

/**
 * The blocks laid out on the match's real time axis — the answer to "where in
 * the game am I?". Each bar sits at its true offset and width within `window`,
 * which runs from the first shown block to the last, so both ends of the track
 * always land on a real bar. Tapping a bar jumps to that block.
 *
 * Replaces the plain dot row: same job — which of N am I on — but placed, so
 * it also says how far in and how much of the match this block covers.
 */
const BlockTimeline: React.FC<{
  spans: BlockSpan[];
  window: BlockSpan;
  current: number;
  onSelect: (i: number) => void;
  /** Prev/next controls, rendered flanking the track so they sit on its
      centre line. A 3-column grid puts the track and the scale in the SAME
      middle column, so the scale's end labels stay flush with the track's
      ends rather than drifting under the arrows. */
  prev?: React.ReactNode;
  next?: React.ReactNode;
}> = ({ spans, window: win, current, onSelect, prev, next }) => {
  const { t } = useTranslation('report');
  const total = Math.max(1, win.end - win.start);
  // A short block still has to be tappable, so bars have a floor width. Kept
  // small enough that it never visibly distorts a realistic block.
  const MIN_PCT = 4;
  return (
    <div className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-x-1.5">
      {prev ?? <span />}
      <div className="relative h-1.5 w-full rounded-full bg-white/15">
        {spans.map((s, i) => {
          const left = ((s.start - win.start) / total) * 100;
          const width = Math.max(MIN_PCT, ((s.end - s.start) / total) * 100);
          const range = `${formatClock(s.start)} – ${formatClock(s.end)}`;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(i)}
              aria-current={i === current ? 'true' : undefined}
              aria-label={t('carousel.goToBlock', { n: i + 1, range })}
              title={range}
              // The visible bar is 6px tall; the ::before stretches the hit
              // area to a thumb-sized band without moving the bar itself.
              className={`absolute top-0 h-full rounded-full outline-none transition-colors duration-200
                before:absolute before:-inset-y-2.5 before:inset-x-0 before:content-['']
                focus-visible:ring-2 focus-visible:ring-white/60 ${
                  i === current ? 'bg-white' : 'bg-white/40 hover:bg-white/70'
                }`}
              style={{
                left: `${left}%`,
                width: `${Math.min(width, 100 - left)}%`,
              }}
            />
          );
        })}
      </div>
      {next ?? <span />}

      {/* Second row: the scale keeps the middle column, so it spans exactly
          the track's width as before — the arrows just gained a row of their
          own to centre against. */}
      <span aria-hidden="true" />
      <div
        className="mt-1 flex w-full items-baseline justify-between gap-2 text-[9px] tracking-[0.1em] text-white/40"
        style={{ fontFamily: 'var(--f-label)' }}
      >
        <span>{formatClock(win.start)}</span>
        <span className="truncate text-[10px] tracking-[0.06em] text-white/85">
          {formatClock(spans[current]?.start ?? win.start)} –{' '}
          {formatClock(spans[current]?.end ?? win.end)}
        </span>
        <span>{formatClock(win.end)}</span>
      </div>
      <span aria-hidden="true" />
    </div>
  );
};

/**
 * Block slider — the top row of the report header. Rendered inside the sticky
 * header slab (via TournamentReport's headerTop) and styled white-on-sky so the
 * blocks read as part of the header rather than a separate bar above it.
 */
const BlockNav: React.FC<{
  current: number;
  total: number;
  spans: BlockSpan[];
  window: BlockSpan;
  onSelect: (i: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onInfo: () => void;
  onBestPractices: () => void;
  onRedoIdentification?: () => void;
}> = ({
  current,
  total,
  spans,
  window: win,
  onSelect,
  onPrev,
  onNext,
  onInfo,
  onBestPractices,
  onRedoIdentification,
}) => {
  const { t } = useTranslation('report');
  const { t: tIdentify } = useTranslation('identifyPlayers');
  const arrow = (
    onClick: () => void,
    disabled: boolean,
    Icon: typeof ChevronLeft,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // -my-3 keeps the 28px tap target but drops what the button contributes
      // to the grid row to 4px, so the 6px track sets the row height instead.
      // Without it the row is arrow-tall and the scale below sits ~15px off
      // the bar; the arrows still centre on the track via items-center.
      className="-my-3 flex h-7 w-7 items-center justify-center text-white/70 transition-opacity disabled:opacity-30"
    >
      <Icon className="h-5 w-5" />
    </button>
  );
  return (
    <div
      className="flex flex-col items-center gap-1 px-2 pb-3 pt-2"
      // The tour's "blocks" step anchors here — only when there is actually
      // more than one block to navigate (otherwise the step self-skips).
      data-tour={total > 1 ? 'blocks' : undefined}
    >
      <span
        className="text-[11px] uppercase tracking-[0.16em] text-white/85"
        style={{ fontFamily: 'var(--f-label)' }}
      >
        {t('carousel.blockOf', { current: current + 1, total })}
      </span>
      {/* The arrows ride inside the timeline so they sit on the track's centre
          line, rather than being centred against this whole column. */}
      <BlockTimeline
        spans={spans}
        window={win}
        current={current}
        onSelect={onSelect}
        prev={total > 1 ? arrow(onPrev, current === 0, ChevronLeft) : undefined}
        next={
          total > 1
            ? arrow(onNext, current === total - 1, ChevronRight)
            : undefined
        }
      />
      {/* Sits between the timeline's scale and the roster avatars below, so it
          needs its own breathing room on top of the column gap. The blocks
          link only makes sense with more than one block; the on-court advice
          always does, so it can stand alone. */}
      <div className="mt-2 flex items-center gap-3 text-white/55">
        {total > 1 && (
          <button
            type="button"
            onClick={onInfo}
            className="flex items-center gap-1"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            <span className="text-[10px]">{t('carousel.whatAreBlocks')}</span>
          </button>
        )}
        <button
          type="button"
          onClick={onBestPractices}
          className="flex items-center gap-1"
        >
          <Lightbulb className="h-3.5 w-3.5" />
          <span className="text-[10px]">{t('carousel.bestPractices')}</span>
        </button>
        {/* Owners only: start their who-is-who and who-they-are over. */}
        {onRedoIdentification ? (
          <button
            type="button"
            onClick={onRedoIdentification}
            className="flex items-center gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="text-[10px]">{tIdentify('redo')}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
};

interface BlockReportCarouselProps {
  segments: SegmentStatistics[];
  identifications: Record<number, PlayerSlot>;
  predictionMode: PredictionMode;
  avatarMap?: Record<string, string>;
  playersCorrespondance?: PlayersCorrespondance;
  /** Whether the splash has lifted — gates the guided tour's auto-start. */
  splashDone?: boolean;
  /** For an owner of the match: shows "Redo identification" in the header. */
  onRedoIdentification?: () => void;
}

/**
 * One block's body: the tournament UI, themed for the report, fed crops with
 * avatar overrides and a name provider where the identified viewer reads "You".
 * The block slider rides in the header (headerTop) so it reads as part of it.
 */
const BlockSlide: React.FC<{
  segment: SegmentStatistics;
  identifiedSlot: PlayerSlot;
  predictionMode: PredictionMode;
  avatarMap?: Record<string, string>;
  playersCorrespondance?: PlayersCorrespondance;
  current: number;
  total: number;
  /** Every visible block's span, and the window they're placed against —
      forwarded straight to the header's timeline. */
  spans: BlockSpan[];
  timelineWindow: BlockSpan;
  /** Whether this slide is the carousel's settled block — only the active
      slide hosts the guided tour (its DOM is the one being spotlighted). */
  active: boolean;
  onSelect: (i: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onInfo: () => void;
  onBestPractices: () => void;
  onRedoIdentification?: () => void;
}> = ({
  segment,
  identifiedSlot,
  predictionMode,
  avatarMap,
  playersCorrespondance,
  current,
  total,
  spans,
  timelineWindow,
  active,
  onSelect,
  onPrev,
  onNext,
  onInfo,
  onBestPractices,
  onRedoIdentification,
}) => {
  const { t } = useTranslation('common');
  const crops = useMemo(
    () => buildPlayerCrops(segment, avatarMap, playersCorrespondance),
    [segment, avatarMap, playersCorrespondance],
  );
  // Every player gets a label describing their place in THIS block — you,
  // your partner, or the opponent on a given side. The slot ids ("player3")
  // are inference tracking ids and mean nothing to a reader, so they are
  // never shown; the roles come from the segment, which assigns one left and
  // one right per pair. NOT_PRESENT is filtered out upstream, so
  // identifiedSlot is a real slot here.
  const names = useMemo(() => {
    const stats = segment.statistics;
    const mine = Object.values(stats.teams).find((team) =>
      team.teammates.includes(identifiedSlot),
    );
    const out: PlayerNamesMap = {};
    for (const pid of Object.keys(stats.players_statistics)) {
      if (pid === identifiedSlot) {
        out[pid] = { short: t('you'), long: t('you') };
      } else if (mine?.teammates.includes(pid)) {
        out[pid] = { short: t('partner'), long: t('partner') };
      } else if (stats.roles[pid]?.primary_role === 'left') {
        out[pid] = { short: t('sideLeft'), long: t('opponentLeft') };
      } else {
        out[pid] = { short: t('sideRight'), long: t('opponentRight') };
      }
    }
    return out;
  }, [segment, identifiedSlot, t]);
  return (
    <PlayerNamesProvider names={names}>
      <TournamentReport
        segment={segment}
        defaultPlayerId={identifiedSlot}
        predictionMode={predictionMode}
        brand="report"
        crops={crops}
        tourHost={active}
        headerTop={
          <BlockNav
            current={current}
            total={total}
            spans={spans}
            window={timelineWindow}
            onSelect={onSelect}
            onPrev={onPrev}
            onNext={onNext}
            onInfo={onInfo}
            onBestPractices={onBestPractices}
            onRedoIdentification={onRedoIdentification}
          />
        }
      />
    </PlayerNamesProvider>
  );
};

export const BlockReportCarousel: React.FC<BlockReportCarouselProps> = ({
  segments,
  identifications,
  predictionMode,
  avatarMap,
  playersCorrespondance,
  splashDone = true,
  onRedoIdentification,
}) => {
  const { t } = useTranslation('report');

  const visibleEntries = useMemo(
    () =>
      segments
        .map((seg, i) => ({ segment: seg, originalIndex: i }))
        .filter(
          ({ originalIndex }) => identifications[originalIndex] !== NOT_PRESENT,
        ),
    [segments, identifications],
  );

  // Each visible block's place on the match clock, and the window they're
  // drawn against: the first block's start to the last one's end.
  //
  // Deliberately NOT the whole session. Anchoring to the session would leave
  // empty track before the first block and after the last one, which reads as
  // "nothing happened then" — but that time is play the carousel dropped
  // (blocks under the duration floor, or ones the viewer sat out), not a lull.
  // Track edges now always coincide with real blocks; the gaps that remain
  // between bars are genuine holes BETWEEN shown blocks.
  const { spans, timelineWindow } = useMemo(() => {
    const s = visibleEntries.map(({ segment }) => ({
      start: segment.statistics.match_summary.start_time,
      end: segment.statistics.match_summary.end_time,
    }));
    return {
      spans: s,
      timelineWindow: {
        start: s.length ? Math.min(...s.map((b) => b.start)) : 0,
        end: s.length ? Math.max(...s.map((b) => b.end)) : 0,
      },
    };
  }, [visibleEntries]);

  // Blocks cross-dissolve in place (no horizontal slide) — the Fade plugin
  // stacks slides and animates opacity, which reads more fluid/minimal and
  // keeps the header anchored while the block content fades between states.
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { align: 'start', loop: false, duration: 28 },
    [Fade()],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = visibleEntries.length;
  const [infoOpen, setInfoOpen] = useState(false);
  const [practicesOpen, setPracticesOpen] = useState(false);

  // The guided tour replaced SwipeHint: same once-per-device trigger, but it
  // walks the whole report (and block navigation is one of its steps).
  const tourSteps = useMemo(() => stepsFor(predictionMode), [predictionMode]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (i: number) => emblaApi?.scrollTo(i),
    [emblaApi],
  );
  const openInfo = useCallback(() => setInfoOpen(true), []);
  const openBestPractices = useCallback(() => setPracticesOpen(true), []);

  // Last settled block, so we reset scroll only on a genuine change — not on
  // mount or embla reInit (resize/reflow).
  const prevIndexRef = useRef(0);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const next = emblaApi.selectedScrollSnap();
    // On a real block change, jump back to the top. Otherwise a shorter
    // incoming slide clamps window.scrollY mid-switch and drags the sticky
    // header down while the roster avatars run their framer-motion `layout`
    // animation (the lurch). Mirrors selectMegaTab's window.scrollTo in
    // TournamentReport.tsx for the mega-tab switch.
    if (next !== prevIndexRef.current) {
      window.scrollTo({ top: 0 });
    }
    prevIndexRef.current = next;
    setCurrentIndex(next);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (total === 0) {
    return (
      <div className="report-theme flex min-h-screen items-center justify-center text-sm text-slate-500">
        {t('carousel.noBlocks')}
      </div>
    );
  }

  return (
    <TourProvider steps={tourSteps} ready={splashDone}>
      <div className="report-theme flex min-h-screen flex-col">
        {/* Carousel — virtualized to current ±1 (heavy court + heatmap per slide).
          The block slider lives inside each slide's header (headerTop).
          overflow-x: clip (not hidden) clips the off-screen blocks horizontally
          without making this a scroll container — so each block's own sticky
          header keeps tracking the page scroll instead of being trapped here. */}
        <div className="flex-1 overflow-x-clip" ref={emblaRef}>
          <div className="flex">
            {visibleEntries.map(({ segment, originalIndex }, i) => (
              <div
                key={originalIndex}
                className="min-w-0"
                style={{
                  flex: '0 0 100%',
                  // Only the active block drives the page height. The blocks embla
                  // keeps mounted alongside it (±1, for the fade) share this flex
                  // row, so a tall neighbour (e.g. another block still on Visão
                  // Geral) would otherwise stretch the row and add dead scroll
                  // below a short active block (e.g. this block's Em Detalhe). Cap
                  // the inactive slides to one viewport so they can't; the active
                  // slide stays uncapped and scrolls its full content.
                  ...(i === currentIndex
                    ? null
                    : { maxHeight: '100vh', overflow: 'hidden' }),
                }}
              >
                {Math.abs(i - currentIndex) <= 1 ? (
                  <BlockSlide
                    segment={segment}
                    identifiedSlot={identifications[originalIndex]}
                    predictionMode={predictionMode}
                    avatarMap={avatarMap}
                    playersCorrespondance={playersCorrespondance}
                    current={currentIndex}
                    total={total}
                    spans={spans}
                    timelineWindow={timelineWindow}
                    active={i === currentIndex}
                    onSelect={scrollTo}
                    onPrev={scrollPrev}
                    onNext={scrollNext}
                    onInfo={openInfo}
                    onBestPractices={openBestPractices}
                    onRedoIdentification={onRedoIdentification}
                  />
                ) : (
                  <div className="min-h-screen" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>

        <InfoBottomSheet open={infoOpen} onClose={() => setInfoOpen(false)} />
        <BestPracticesBottomSheet
          open={practicesOpen}
          onClose={() => setPracticesOpen(false)}
        />
        {/* Fixed overlay — a sibling of the carousel on purpose: inside the
            embla track its transformed ancestors would trap the fixed
            positioning; here it also survives block changes. */}
        <GuidedTourLayer />
      </div>
    </TourProvider>
  );
};

export default BlockReportCarousel;
